/**
 * 教材ハイライト機能 (U4)
 *
 * 教材記事内のテキストを選択 → ポップオーバー表示 → ハイライト保存。
 * localStorage に {articleId, text, color, createdAt} で永続化。
 * 章を再表示時に <mark> でラップして表示。
 *
 * 選ぶと画面下に黒い帯が出る（黄／赤／閉じる）。
 * 全ハイライト一覧は「学ぶ」のタイルと記事の右上からシートで開く。
 */

import { escapeHtml, toast, openSheet, icon, buzz, formatDate } from './ui.js';

const STORAGE_KEY = 'zn.highlights.v1';

export function loadHighlights() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch { return []; }
}

function saveHighlights(arr) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(arr));
}

export function addHighlight(articleId, articleTitle, text, color = 'yellow') {
  const arr = loadHighlights();
  arr.push({
    id: `hl_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 5)}`,
    articleId,
    articleTitle,
    text: String(text || '').slice(0, 400),
    color,
    createdAt: Date.now(),
  });
  saveHighlights(arr);
}

export function removeHighlight(id) {
  const arr = loadHighlights().filter((h) => h.id !== id);
  saveHighlights(arr);
}

/**
 * 記事 HTML に既存ハイライトを反映 (<mark> でラップ)
 * 同じ articleId のハイライトを全て検索し、HTML 内で最初に出現する箇所をラップ。
 */
export function injectHighlights(htmlString, articleId) {
  const items = loadHighlights().filter((h) => h.articleId === articleId);
  if (items.length === 0) return htmlString;
  let result = htmlString;
  for (const h of items) {
    const escaped = escapeRegExp(h.text);
    const cls = h.color === 'red' ? 'hl hl--red' : 'hl';
    const re = new RegExp(escaped, '');
    result = result.replace(re, `<mark class="${cls}" data-hl-id="${escapeHtml(h.id)}">$&</mark>`);
  }
  return result;
}

function escapeRegExp(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * 記事内のテキスト選択 → 画面下の黒い帯（黄／赤）
 * document のリスナーは一度だけ登録し、表示中の記事を差し替える。
 */
let target = null; // { container, articleId, articleTitle }
let bar = null;
let pending = '';
let bound = false;

export function bindSelectionToolbar(container, articleId, articleTitle) {
  target = { container, articleId, articleTitle };
  hideBar();
  if (bound) return;
  bound = true;
  let timer = null;
  const handle = () => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      if (!target || !document.getElementById('screen-learn-article')?.classList.contains('screen--active')) { hideBar(); return; }
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed || sel.rangeCount === 0) { hideBar(); return; }
      if (!target.container.contains(sel.anchorNode)) { hideBar(); return; }
      const text = sel.toString().trim();
      if (text.length < 2 || text.length > 400) { hideBar(); return; }
      pending = text;
      showBar();
    }, 250);
  };
  document.addEventListener('selectionchange', handle);
}

function showBar() {
  if (bar) return;
  bar = document.createElement('div');
  bar.className = 'hl-bar';
  bar.innerHTML = `
    <button type="button" data-c="yellow"><span style="width:12px;height:12px;background:var(--color-hl-yellow)"></span>ハイライト（黄）</button>
    <button type="button" data-c="red"><span style="width:12px;height:3px;background:var(--color-hl-red)"></span>ハイライト（赤）</button>
    <button type="button" data-x aria-label="閉じる">${icon('close')}</button>`;
  // ボタンを押しても選択が外れないように
  bar.addEventListener('pointerdown', (e) => e.preventDefault());
  bar.querySelectorAll('[data-c]').forEach((b) => b.addEventListener('click', () => {
    if (!target || !pending) return;
    addHighlight(target.articleId, target.articleTitle, pending, b.dataset.c);
    buzz(8);
    toast(b.dataset.c === 'red' ? 'ハイライト（赤）を保存しました' : 'ハイライト（黄）を保存しました');
    window.getSelection()?.removeAllRanges();
    hideBar();
    const id = target.articleId;
    import('./learn.js').then((m) => m.openLearnArticle(id));
  }));
  bar.querySelector('[data-x]').addEventListener('click', () => { window.getSelection()?.removeAllRanges(); hideBar(); });
  document.getElementById('app').appendChild(bar);
}

function hideBar() {
  bar?.remove();
  bar = null;
}

/** マイハイライト一覧（シート） */
export function openMyHighlights() {
  const body = document.createElement('div');
  const sheet = openSheet({ en: 'highlights', title: 'マイハイライト', body, full: true });
  const paint = () => {
    const items = loadHighlights().sort((a, b) => b.createdAt - a.createdAt);
    if (items.length === 0) {
      body.innerHTML = '<p class="sub" style="margin:24px;font-size:14px;line-height:1.7">ハイライトはまだありません。記事の文字を長押しして選ぶと登録できます。</p>';
      return;
    }
    body.innerHTML = items.map((h) => `
      <div class="hl-item" data-article-id="${escapeHtml(h.articleId)}" role="button" tabindex="0">
        <span class="hl-item__bar" style="background:${h.color === 'red' ? 'var(--color-hl-red)' : 'var(--color-hl-yellow)'}"></span>
        <div style="flex:1;min-width:0">
          <div class="sub" style="font:500 12px/1.3 var(--font-jp-sans)">${escapeHtml(h.articleTitle)}</div>
          <div style="font:400 14px/1.7 var(--font-jp-serif);margin-top:4px">${escapeHtml(h.text)}</div>
          <div class="en sub" style="font-size:11px;margin-top:6px">${escapeHtml(formatDate(h.createdAt))}</div>
        </div>
        <button class="bar__btn" type="button" data-hl-delete="${escapeHtml(h.id)}" aria-label="削除">${icon('trash', 18)}</button>
      </div>`).join('');
    body.querySelectorAll('.hl-item').forEach((el) => el.addEventListener('click', async (e) => {
      if (e.target.closest('[data-hl-delete]')) return;
      await sheet.close();
      import('./learn.js').then((m) => m.openLearnArticle(el.dataset.articleId));
    }));
    body.querySelectorAll('[data-hl-delete]').forEach((el) => el.addEventListener('click', (e) => {
      e.stopPropagation();
      removeHighlight(el.dataset.hlDelete);
      toast('ハイライトを削除しました');
      paint();
    }));
  };
  paint();
}
